import {
  Box,
  Typography,
  Container,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { Helmet } from "react-helmet-async";

function CookiePolicy() {
  const cookieTypes = [
    {
      name: "Essential Cookies",
      purpose: "Required for the website to function properly",
      duration: "Session or 1 year",
      examples: "Authentication, security, form submissions",
    },
    {
      name: "Analytics Cookies",
      purpose: "Help us understand how visitors use our website",
      duration: "Up to 2 years",
      examples: "Google Analytics, page views, user behavior",
    },
    {
      name: "Functional Cookies",
      purpose: "Remember your preferences and settings",
      duration: "Up to 1 year",
      examples: "Language preferences, theme settings",
    },
    {
      name: "Marketing Cookies",
      purpose: "Used to deliver relevant advertisements",
      duration: "Up to 1 year",
      examples: "Social media integration, targeted ads",
    },
  ];

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Helmet>
        <title>Cookie Policy — Loops</title>
        <meta name="description" content="Read Loops' Cookie Policy to understand how we use cookies to improve your experience on our food deals platform." />
        <link rel="canonical" href="https://loopsfood.com/cookies" />
      </Helmet>
      <Typography
        variant="h3"
        sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
      >
        Cookie Policy
      </Typography>

      <Typography
        variant="body1"
        sx={{ color: "#6b7280", mb: 6, textAlign: "center" }}
      >
        Last updated: January 1, 2025
      </Typography>

      <Divider sx={{ mb: 6 }} />

      <Box
        sx={{
          "& h4": { fontWeight: 600, mt: 4, mb: 2 },
          "& p": { mb: 2, lineHeight: 1.7 },
        }}
      >
        <Typography variant="h4">What Are Cookies?</Typography>
        <Typography variant="body1">
          Cookies are small text files that are placed on your computer or
          mobile device when you visit our website. They are widely used to make
          websites work more efficiently and to provide information to website
          owners.
        </Typography>

        <Typography variant="h4">How We Use Cookies</Typography>
        <Typography variant="body1">
          Loops uses cookies to enhance your experience on our platform, analyze
          website traffic, and for marketing purposes. We use both first-party
          cookies (set by us) and third-party cookies (set by our partners).
        </Typography>

        <Typography variant="h4">Types of Cookies We Use</Typography>
        <TableContainer component={Paper} sx={{ mt: 3, mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Cookie Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Examples</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cookieTypes.map((cookie, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontWeight: 500 }}>{cookie.name}</TableCell>
                  <TableCell>{cookie.purpose}</TableCell>
                  <TableCell>{cookie.duration}</TableCell>
                  <TableCell>{cookie.examples}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="h4">Managing Your Cookie Preferences</Typography>
        <Typography variant="body1">
          You can control and manage cookies in several ways:
        </Typography>
        <Box component="ul" sx={{ pl: 4, mb: 2 }}>
          <li>
            <strong>Browser Settings:</strong> Most browsers allow you to block
            or delete cookies through their settings menu
          </li>
          <li>
            <strong>Cookie Banner:</strong> When you first visit our site, you
            can choose which types of cookies to accept
          </li>
          <li>
            <strong>Opt-out Links:</strong> Some third-party services provide
            direct opt-out mechanisms
          </li>
          <li>
            <strong>Do Not Track:</strong> We respect browser "Do Not Track"
            signals where possible
          </li>
        </Box>

        <Typography variant="h4">Essential Cookies</Typography>
        <Typography variant="body1">
          Some cookies are essential for our website to function properly. These
          cannot be disabled as they are necessary for:
        </Typography>
        <Box component="ul" sx={{ pl: 4, mb: 2 }}>
          <li>User authentication and security</li>
          <li>Remembering your login status</li>
          <li>Processing payments and transactions</li>
          <li>Maintaining your session during your visit</li>
        </Box>

        <Typography variant="h4">Third-Party Cookies</Typography>
        <Typography variant="body1">
          We work with trusted third-party partners who may set cookies on our
          website. These include:
        </Typography>
        <Box component="ul" sx={{ pl: 4, mb: 2 }}>
          <li>
            <strong>Google Analytics:</strong> For website analytics and
            performance monitoring
          </li>
          <li>
            <strong>Payment Processors:</strong> For secure payment processing
          </li>
          <li>
            <strong>Social Media Platforms:</strong> For social sharing and
            integration
          </li>
          <li>
            <strong>Customer Support:</strong> For live chat and support
            services
          </li>
        </Box>

        <Typography variant="h4">Cookie Consent</Typography>
        <Typography variant="body1">
          By continuing to use our website after seeing our cookie notice, you
          consent to our use of cookies as described in this policy. You can
          withdraw your consent at any time by adjusting your browser settings
          or contacting us.
        </Typography>

        <Typography variant="h4">Changes to This Cookie Policy</Typography>
        <Typography variant="body1">
          We may update this Cookie Policy from time to time to reflect changes
          in our practices or for legal reasons. We will notify you of any
          significant changes by posting the updated policy on our website.
        </Typography>

        <Typography variant="h4">Contact Us</Typography>
        <Typography variant="body1">
          If you have any questions about our use of cookies, please contact us
          at:
        </Typography>
        <Box sx={{ pl: 2, mt: 2 }}>
          <Typography variant="body1">Email: privacy@loops.com</Typography>
          <Typography variant="body1">Phone: +1 (555) 123-4567</Typography>
        </Box>
      </Box>
    </Container>
  );
}

export default CookiePolicy;
